import React, { useMemo, useState } from 'react';
import {
  Table,
  Typography,
  Space,
  Button,
  Tag,
  Spin,
  Input,
  Form,
  InputNumber,
  DatePicker,
  App,
  Divider,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  FileTextOutlined,
  HistoryOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/api';
import { FileUploader } from '../components/file/FileUploader';
import { ACCEPTED_DOCUMENT_LABEL, ACCEPTED_DOCUMENT_TYPES } from '../constants/documentAccept';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { useAuthStore } from '../stores/authStore';
import { withDevModalFilePath } from '../components/common';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Modal } = withDevModalFilePath('client/src/pages/OfficialInspectionDocumentsPage.tsx');

interface OfficialReport {
  id: string;
  status: string;
  reportYear?: number | null;
  submittedAt?: string | Date | null;
  acceptedAt?: string | Date | null;
  returnedAt?: string | Date | null;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
  submittedTo?: string | null;
  returnReason?: string | null;
  documentFileVersionId?: string | null;
}

interface OfficialCell {
  documentTypeId: string;
  documentId: string | null;
  sourceDocumentFileId?: string | null;
  status: string;
  latestReport: OfficialReport | null;
  nextDueAt: string | Date | null;
  overdue: boolean;
}

interface OfficialBuildingRow {
  locationId: string;
  name: string;
  categoryLabel?: string | null;
  cells: OfficialCell[];
}

interface OfficialDocumentType {
  id: string;
  name: string;
  cycleMonths?: number | null;
}

type OfficialHistoryRow = {
  report: OfficialReport;
  fileVersion?: { versionNumber?: number | null } | null;
};

const normalizeOfficialHistoryRow = (value: unknown): OfficialHistoryRow | null => {
  if (
    !value ||
    typeof value !== 'object' ||
    !('report' in value) ||
    !value.report ||
    typeof value.report !== 'object'
  ) {
    return null;
  }

  const rowValue = value as Record<string, unknown>;
  const reportValue = value.report as Record<string, unknown>;
  if (typeof reportValue.id !== 'string') {
    return null;
  }

  return {
    report: {
      id: reportValue.id,
      status: typeof reportValue.status === 'string' ? reportValue.status : null,
      reportYear: typeof reportValue.reportYear === 'number' ? reportValue.reportYear : null,
      submittedAt: typeof reportValue.submittedAt === 'string' ? reportValue.submittedAt : null,
      acceptedAt: typeof reportValue.acceptedAt === 'string' ? reportValue.acceptedAt : null,
      returnedAt: typeof reportValue.returnedAt === 'string' ? reportValue.returnedAt : null,
      updatedAt: typeof reportValue.updatedAt === 'string' ? reportValue.updatedAt : null,
      createdAt: typeof reportValue.createdAt === 'string' ? reportValue.createdAt : null,
      submittedTo: typeof reportValue.submittedTo === 'string' ? reportValue.submittedTo : null,
      returnReason: typeof reportValue.returnReason === 'string' ? reportValue.returnReason : null,
      documentFileVersionId:
        typeof reportValue.documentFileVersionId === 'string' ? reportValue.documentFileVersionId : null,
    },
    fileVersion:
      rowValue.fileVersion && typeof rowValue.fileVersion === 'object'
        ? {
            versionNumber:
              typeof (rowValue.fileVersion as { versionNumber?: unknown }).versionNumber === 'number'
                ? (rowValue.fileVersion as { versionNumber: number }).versionNumber
                : null,
          }
        : null,
  };
};

const statusLabelMap: Record<string, { label: string; color: string }> = {
  missing: { label: '未登録', color: 'red' },
  draft: { label: '未提出', color: 'default' },
  submitted: { label: '提出済', color: 'blue' },
  accepted: { label: '承認済', color: 'green' },
  returned: { label: '差戻し', color: 'orange' },
};

const getReadableDate = (value?: string | Date | null) => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const formatDate = (value?: string | Date | null) => {
  const date = getReadableDate(value);
  return date ? date.toLocaleDateString('ja-JP') : '-';
};

const normalizeDocumentFileName = (fileName?: string | null) => {
  const safeName = (fileName ?? '').trim();
  if (!safeName) return '';
  return safeName.toLowerCase();
};

const resolveStatus = (cell: OfficialCell) => {
  if (cell.status === 'missing') {
    return statusLabelMap.missing;
  }
  const reportStatus = cell.latestReport?.status;
  if (reportStatus && statusLabelMap[reportStatus]) {
    return statusLabelMap[reportStatus];
  }
  if (cell.status && statusLabelMap[cell.status]) {
    return statusLabelMap[cell.status];
  }
  return statusLabelMap.draft;
};

const resolveLatestReportDate = (report?: OfficialReport | null) => {
  if (!report) {
    return null;
  }
  return (
    getReadableDate(report.acceptedAt) ||
    getReadableDate(report.submittedAt) ||
    getReadableDate(report.returnedAt) ||
    getReadableDate(report.updatedAt) ||
    getReadableDate(report.createdAt) ||
    null
  );
};

const reportStatusLabel = (status?: string | null) => {
  if (!status) {
    return '-';
  }
  const mapping = statusLabelMap[status];
  return mapping?.label ?? status;
};

export const OfficialInspectionDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const { data: profile } = trpc.user.getProfile.useQuery();
  const role = profile?.role ?? user?.role ?? 'viewer';
  const canSubmit = ['user', 'manager', 'admin'].includes(role);
  const canApprove = ['manager', 'admin'].includes(role);

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.document.officialMatrix.useQuery();

  const [searchTerm, setSearchTerm] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{
    locationId: string;
    locationName: string;
    documentTypeId: string;
    documentTypeName: string;
    documentId?: string | null;
    documentFileId?: string | null;
    latestReport?: OfficialReport | null;
  } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [historyDocumentId, setHistoryDocumentId] = useState<string | null>(null);
  const [returnReportId, setReturnReportId] = useState<string | null>(null);

  const [createForm] = Form.useForm();
  const [versionForm] = Form.useForm();
  const [submitForm] = Form.useForm();
  const [returnForm] = Form.useForm();

  const createMutation = trpc.document.create.useMutation({
    onSuccess: async () => {
      message.success('書類を登録しました');
      setCreateModalOpen(false);
      setUploadedFiles([]);
      await utils.document.officialMatrix.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '書類登録に失敗しました');
    },
  });

  const addVersionMutation = trpc.document.addFileVersion.useMutation({
    onSuccess: async () => {
      message.success('新しいバージョンを登録しました');
      setVersionModalOpen(false);
      setUploadedFiles([]);
      await utils.document.officialMatrix.invalidate();
    },
    onError: (error) => {
      message.error(error.message || 'バージョン登録に失敗しました');
    },
  });

  const submitMutation = trpc.document.submitOfficial.useMutation({
    onSuccess: async () => {
      message.success('提出しました');
      setSubmitModalOpen(false);
      await utils.document.officialMatrix.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '提出に失敗しました');
    },
  });

  const acceptMutation = trpc.document.acceptInspection.useMutation({
    onSuccess: async () => {
      message.success('承認しました');
      await utils.document.officialMatrix.invalidate();
      if (historyDocumentId) {
        await utils.document.officialHistory.invalidate();
      }
    },
    onError: (error) => {
      message.error(error.message || '承認に失敗しました');
    },
  });

  const returnMutation = trpc.document.returnInspection.useMutation({
    onSuccess: async () => {
      message.success('差戻ししました');
      setReturnModalOpen(false);
      setReturnReportId(null);
      await utils.document.officialMatrix.invalidate();
      if (historyDocumentId) {
        await utils.document.officialHistory.invalidate();
      }
    },
    onError: (error) => {
      message.error(error.message || '差戻しに失敗しました');
    },
  });

  const { data: historyData, isLoading: historyLoading } = trpc.document.officialHistory.useQuery(
    historyDocumentId ?? '',
    {
      enabled: Boolean(historyDocumentId && historyModalOpen),
    }
  );

  const officialTypes = useMemo(
    () => (data?.documentTypes ?? []) as OfficialDocumentType[],
    [data?.documentTypes]
  );
  const buildings = useMemo(
    () => (data?.buildings ?? []) as OfficialBuildingRow[],
    [data?.buildings]
  );

  const filteredBuildings = useMemo(() => {
    if (!searchTerm.trim()) {
      return buildings;
    }
    const keyword = searchTerm.trim().toLowerCase();
    return buildings.filter((row) =>
      row.name.toLowerCase().includes(keyword) ||
      (row.categoryLabel ?? '').toLowerCase().includes(keyword)
    );
  }, [buildings, searchTerm]);

  const normalizedHistoryData = useMemo(
    () => (historyData ?? []).flatMap((row) => {
      const normalized = normalizeOfficialHistoryRow(row);
      return normalized ? [normalized] : [];
    }),
    [historyData]
  );

  const handleOpenCreate = React.useCallback((row: OfficialBuildingRow, type: OfficialDocumentType) => {
    setSelectedCell({
      locationId: row.locationId,
      locationName: row.name,
      documentTypeId: type.id,
      documentTypeName: type.name,
    });
    setUploadedFiles([]);
    createForm.setFieldsValue({
      title: `${row.name} ${type.name}`,
    });
    setCreateModalOpen(true);
  }, [createForm]);

  const handleOpenAddVersion = React.useCallback((row: OfficialBuildingRow, type: OfficialDocumentType, cell: OfficialCell) => {
    setSelectedCell({
      locationId: row.locationId,
      locationName: row.name,
      documentTypeId: type.id,
      documentTypeName: type.name,
      documentId: cell.documentId,
      documentFileId: cell.sourceDocumentFileId ?? null,
      latestReport: cell.latestReport,
    });
    setUploadedFiles([]);
    versionForm.resetFields();
    setVersionModalOpen(true);
  }, [versionForm]);

  const handleOpenSubmit = React.useCallback((row: OfficialBuildingRow, type: OfficialDocumentType, cell: OfficialCell) => {
    setSelectedCell({
      locationId: row.locationId,
      locationName: row.name,
      documentTypeId: type.id,
      documentTypeName: type.name,
      documentId: cell.documentId,
      documentFileId: cell.sourceDocumentFileId ?? null,
      latestReport: cell.latestReport,
    });
    submitForm.setFieldsValue({
      reportYear: new Date().getFullYear(),
      submittedTo: cell.latestReport?.submittedTo ?? undefined,
      reportPeriod: undefined,
    });
    setSubmitModalOpen(true);
  }, [submitForm]);

  const handleOpenHistory = React.useCallback((cell: OfficialCell) => {
    if (!cell.documentId) {
      return;
    }
    setHistoryDocumentId(cell.documentId);
    setHistoryModalOpen(true);
  }, []);

  const handleOpenReturn = React.useCallback((reportId: string) => {
    setReturnReportId(reportId);
    returnForm.resetFields();
    setReturnModalOpen(true);
  }, [returnForm]);

  const handleCreate = async () => {
    if (!selectedCell) {
      return;
    }

    const values = await createForm.validateFields();

    if (uploadedFiles.length === 0) {
      message.warning('ファイルをアップロードしてください');
      return;
    }

    createMutation.mutate({
      title: values.title,
      description: values.description ?? undefined,
      documentTypeId: selectedCell.documentTypeId,
      association: {
        entityType: 'location',
        entityId: selectedCell.locationId,
      },
      files: uploadedFiles.map((file) => ({ fileId: file.id })),
    });
  };

  const handleAddVersion = async () => {
    if (!selectedCell?.documentFileId) {
      message.warning('対象ファイルが見つかりません');
      return;
    }
    const values = await versionForm.validateFields();

    if (uploadedFiles.length === 0) {
      message.warning('ファイルをアップロードしてください');
      return;
    }

    addVersionMutation.mutate({
      documentFileId: selectedCell.documentFileId,
      fileId: uploadedFiles[0].id,
      changesNote: values.changesNote || undefined,
    });
  };

  const handleSubmit = async () => {
    if (!selectedCell?.documentId) {
      return;
    }

    const values = await submitForm.validateFields();
    const [start, end] = values.reportPeriod ?? [];

    submitMutation.mutate({
      documentId: selectedCell.documentId,
      reportYear: values.reportYear,
      submittedTo: values.submittedTo || undefined,
      reportPeriodStart: start ? start.toDate() : undefined,
      reportPeriodEnd: end ? end.toDate() : undefined,
    });
  };

  const handleReturn = async () => {
    if (!returnReportId) {
      return;
    }
    const values = await returnForm.validateFields();
    returnMutation.mutate({
      reportId: returnReportId,
      reason: values.reason,
    });
  };

  const columns: ColumnsType<OfficialBuildingRow> = useMemo(() => {
    const baseColumns: ColumnsType<OfficialBuildingRow> = [
      {
        title: '建物',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 220,
        render: (value: string) => <Text strong>{value}</Text>,
      },
    ];

    const typeColumns = officialTypes.map((type) => ({
      title: (
        <Space direction="vertical" size={0}>
          <Text strong>{type.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {type.cycleMonths ? `提出周期: ${type.cycleMonths}ヶ月` : '提出周期: -'}
          </Text>
        </Space>
      ),
      key: type.id,
      width: 320,
      render: (_: unknown, row: OfficialBuildingRow) => {
        const cell = row.cells.find((item) => item.documentTypeId === type.id);
        if (!cell) {
          return '-';
        }
        const status = resolveStatus(cell);
        const latestDate = resolveLatestReportDate(cell.latestReport);
        const latestDateLabel = latestDate ? latestDate.toLocaleDateString('ja-JP') : '-';
        const nextDueLabel = formatDate(cell.nextDueAt);

        return (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space size="small" wrap>
              <Tag color={status.color}>{status.label}</Tag>
              {cell.overdue && <Tag color="red">期限超過</Tag>}
            </Space>

            <Text type="secondary" style={{ fontSize: 12 }}>
              最新提出: {latestDateLabel}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              次回期限: {nextDueLabel}
            </Text>

            {cell.latestReport?.status === 'returned' && cell.latestReport.returnReason && (
              <Text type="danger" style={{ fontSize: 12 }}>
                差戻し理由: {cell.latestReport.returnReason}
              </Text>
            )}

            <Space size="small" wrap>
              {cell.documentId ? (
                <>
                  {canSubmit && (
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      disabled={!cell.sourceDocumentFileId}
                      onClick={() => handleOpenAddVersion(row, type, cell)}
                    >
                      改版
                    </Button>
                  )}
                  {canSubmit && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => handleOpenSubmit(row, type, cell)}
                    >
                      提出
                    </Button>
                  )}
                  <Button
                    size="small"
                    icon={<HistoryOutlined />}
                    onClick={() => handleOpenHistory(cell)}
                  >
                    履歴
                  </Button>
                </>
              ) : (
                canSubmit && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => handleOpenCreate(row, type)}
                  >
                    書類登録
                  </Button>
                )
              )}
              {cell.latestReport?.status === 'submitted' && canApprove && cell.latestReport.id && (
                <>
                  <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => acceptMutation.mutate({ reportId: cell.latestReport.id })}
                    loading={acceptMutation.isPending}
                  >
                    承認
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleOpenReturn(cell.latestReport!.id)}
                  >
                    差戻し
                  </Button>
                </>
              )}
            </Space>
          </Space>
        );
      },
    }));

    return [...baseColumns, ...typeColumns];
  }, [
    officialTypes,
    canSubmit,
    canApprove,
    acceptMutation,
    handleOpenAddVersion,
    handleOpenSubmit,
    handleOpenHistory,
    handleOpenCreate,
    handleOpenReturn,
  ]);

  const historyColumns: ColumnsType<OfficialHistoryRow> = [
    {
      title: 'ステータス',
      dataIndex: ['report', 'status'],
      key: 'status',
      render: (value: string) => reportStatusLabel(value),
    },
    {
      title: '提出年',
      dataIndex: ['report', 'reportYear'],
      key: 'reportYear',
      render: (value: number) => value ?? '-',
    },
    {
      title: '提出日',
      dataIndex: ['report', 'submittedAt'],
      key: 'submittedAt',
      render: (value: string | Date | null) => formatDate(value),
    },
    {
      title: '承認日',
      dataIndex: ['report', 'acceptedAt'],
      key: 'acceptedAt',
      render: (value: string | Date | null) => formatDate(value),
    },
    {
      title: '差戻し日',
      dataIndex: ['report', 'returnedAt'],
      key: 'returnedAt',
      render: (value: string | Date | null) => formatDate(value),
    },
    {
      title: 'バージョン',
      dataIndex: ['fileVersion', 'versionNumber'],
      key: 'versionNumber',
      render: (value: number) => value ?? '-',
    },
    {
      title: '提出先',
      dataIndex: ['report', 'submittedTo'],
      key: 'submittedTo',
      render: (value: string | null) => value || '-',
    },
    {
      title: '差戻し理由',
      dataIndex: ['report', 'returnReason'],
      key: 'returnReason',
      render: (value: string | null) => value || '-',
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Title level={3} style={{ margin: 0 }}>法定点検書類</Title>
            <Tag color="blue">建物のみ</Tag>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void utils.document.officialMatrix.invalidate()} />
            <Button onClick={() => navigate('/app/documents')}>書類へ</Button>
          </Space>
        </Space>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <Input.Search
            placeholder="建物名・カテゴリで検索"
            allowClear
            style={{ maxWidth: 320 }}
            onSearch={setSearchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            value={searchTerm}
          />
          <Space size="small">
            <Tag color="red">未登録</Tag>
            <Tag color="default">未提出</Tag>
            <Tag color="blue">提出済</Tag>
            <Tag color="green">承認済</Tag>
            <Tag color="orange">差戻し</Tag>
          </Space>
        </Space>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            rowKey={(row) => row.locationId}
            dataSource={filteredBuildings}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: officialTypes.length === 0
                ? '法定点検タイプが有効化されていません'
                : '建物カテゴリのエリアがありません',
            }}
          />
        )}
      </Space>

      <Modal
        title="法定点検書類の登録"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setSelectedCell(null);
          setUploadedFiles([]);
        }}
        onOk={() => void handleCreate()}
        okText="登録"
        cancelText="キャンセル"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            {selectedCell?.locationName} / {selectedCell?.documentTypeName}
          </Text>
          <Form layout="vertical" form={createForm}>
            <Form.Item
              name="title"
              label="書類名"
              rules={[{ required: true, message: '書類名を入力してください' }]}
            >
              <Input placeholder="例: 2025年度 防火設備検査" maxLength={255} />
            </Form.Item>
            <Form.Item
              name="description"
              label="説明"
              valuePropName="value"
              trigger="onChange"
            >
              <RichTextEditor placeholder="書類の詳細や補足を記載" minHeight={160} />
            </Form.Item>
          </Form>
          <Divider style={{ margin: '8px 0' }} />
          <Text type="secondary">アップロード済み: {uploadedFiles.length}件</Text>
          {selectedCell && (
            <FileUploader
              entityType="location"
              entityId={selectedCell.locationId}
              category="document"
              accept={ACCEPTED_DOCUMENT_TYPES}
              acceptLabel={ACCEPTED_DOCUMENT_LABEL}
              maxFiles={10}
              maxSize={25}
              disallowDuplicateNames
              existingFileNames={uploadedFiles.map((file) => file.name)}
              normalizeDuplicateName={normalizeDocumentFileName}
              onUploadSuccess={(file) => {
                if (file?.id) {
                  const name = (file?.originalName ?? file?.fileName ?? file?.name ?? '').toString();
                  setUploadedFiles((prev) => {
                    const normalized = normalizeDocumentFileName(name);
                    const exists = prev.some((item) => normalizeDocumentFileName(item.name) === normalized);
                    if (exists) {
                      message.error('同名ファイルはアップロードできません');
                      return prev;
                    }
                    return [...prev, { id: file.id, name }];
                  });
                }
              }}
            />
          )}
        </Space>
      </Modal>

      <Modal
        title="改版アップロード"
        open={versionModalOpen}
        onCancel={() => {
          setVersionModalOpen(false);
          setSelectedCell(null);
          setUploadedFiles([]);
        }}
        onOk={() => void handleAddVersion()}
        okText="登録"
        cancelText="キャンセル"
        confirmLoading={addVersionMutation.isPending}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            {selectedCell?.locationName} / {selectedCell?.documentTypeName}
          </Text>
          <Form layout="vertical" form={versionForm}>
            <Form.Item name="changesNote" label="変更メモ">
              <Input.TextArea rows={3} maxLength={500} placeholder="改版内容を簡単に記載" />
            </Form.Item>
          </Form>
          <Divider style={{ margin: '8px 0' }} />
          <Text type="secondary">アップロード済み: {uploadedFiles.length}件</Text>
          {selectedCell?.documentId && (
            <FileUploader
              entityType="location"
              entityId={selectedCell.locationId}
              category="document"
              accept={ACCEPTED_DOCUMENT_TYPES}
              acceptLabel={ACCEPTED_DOCUMENT_LABEL}
              maxFiles={1}
              maxSize={25}
              onUploadSuccess={(file) => {
                if (file?.id) {
                  const name = (file?.originalName ?? file?.fileName ?? file?.name ?? '').toString();
                  setUploadedFiles([{ id: file.id, name }]);
                }
              }}
            />
          )}
        </Space>
      </Modal>

      <Modal
        title="提出情報"
        open={submitModalOpen}
        onCancel={() => {
          setSubmitModalOpen(false);
          setSelectedCell(null);
        }}
        onOk={() => void handleSubmit()}
        okText="提出"
        cancelText="キャンセル"
        confirmLoading={submitMutation.isPending}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            {selectedCell?.locationName} / {selectedCell?.documentTypeName}
          </Text>
          <Form layout="vertical" form={submitForm}>
            <Form.Item
              name="reportYear"
              label="提出年"
              rules={[{ required: true, message: '提出年を入力してください' }]}
            >
              <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="submittedTo" label="提出先">
              <Input placeholder="例: 県庁 建築指導課" maxLength={120} />
            </Form.Item>
            <Form.Item name="reportPeriod" label="報告対象期間">
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="提出履歴"
        open={historyModalOpen}
        onCancel={() => {
          setHistoryModalOpen(false);
          setHistoryDocumentId(null);
        }}
        footer={null}
        width={900}
        destroyOnHidden
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            rowKey={(row) => row.report.id}
            dataSource={normalizedHistoryData}
            columns={historyColumns}
            pagination={false}
          />
        )}
      </Modal>

      <Modal
        title="差戻し理由"
        open={returnModalOpen}
        onCancel={() => {
          setReturnModalOpen(false);
          setReturnReportId(null);
        }}
        onOk={() => void handleReturn()}
        okText="差戻し"
        cancelText="キャンセル"
        okButtonProps={{ danger: true }}
        confirmLoading={returnMutation.isPending}
        destroyOnHidden
      >
        <Form layout="vertical" form={returnForm}>
          <Form.Item
            name="reason"
            label="差戻し理由"
            rules={[{ required: true, message: '差戻し理由を入力してください' }]}
          >
            <Input.TextArea rows={4} maxLength={500} placeholder="例: 提出先の指定が不足しています" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OfficialInspectionDocumentsPage;
